import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HeartHandshake, User, Sprout, Brain, Plus } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";

const offers = [
  {
    icon: HeartHandshake,
    title: "Voor ouders",
    paragraphs: [
      "Ouder zijn is misschien wel de mooiste, maar tegelijk een van de meest uitdagende rollen die je in het leven opneemt.",
      "Soms bots je op onzekerheid. Je vraagt je af of je het wel goed doet. Je probeert er te zijn voor je kind, terwijl je zelf moe bent, piekert of voortdurend alle ballen in de lucht probeert te houden.",
      "Kinderen hebben geen nood aan perfecte ouders. Ze hebben nood aan ouders die zichzelf mogen zijn. Ouders die durven stilstaan bij wat moeilijk loopt, die mild kunnen zijn voor zichzelf en die stap voor stap opnieuw vertrouwen vinden.",
      "Wanneer jij als ouder meer rust ervaart, steviger in je schoenen staat en beter begrijpt wat jij én je kind nodig hebben, ontstaat er opnieuw ruimte voor verbinding. Dat is wat ik iedere ouder toewens en waar ik graag samen met jullie naartoe werk.",
    ],
  },
  {
    icon: User,
    title: "Volwassenen",
    paragraphs: [
      "Soms vraagt het leven veel van je. Je voelt dat je voortdurend aanstaat. Je piekert, twijfelt aan jezelf of het lukt niet meer om de rust te vinden waar je zo naar verlangt.",
      "Misschien wil je sterker leren omgaan met stress, opnieuw meer zelfvertrouwen voelen of gewoon even stilstaan bij jezelf. Samen gaan we op zoek naar wat jij nodig hebt om opnieuw dichter bij jezelf te komen, vanuit meer rust en veerkracht.",
    ],
  },
  {
    icon: Sprout,
    title: "Kinderen en jongeren",
    paragraphs: [
      "Elk kind en elke jongere beleeft wel eens een periode waarin het moeilijk gaat. Gevoelens die overspoelen, onzekerheid, stress, faalangst, sociale moeilijkheden of ingrijpende gebeurtenissen kunnen ervoor zorgen dat een kind vastloopt.",
      "Praten over wat er vanbinnen leeft is voor kinderen en jongeren niet altijd vanzelfsprekend. Daarom vertrekken we niet alleen vanuit woorden, maar ook vanuit spel, creatieve werkvormen en ervaringsgerichte activiteiten.",
      "Via spel en creativiteit krijgen gevoelens, gedachten en ervaringen vaak op een natuurlijke manier een plaats. Dat maakt het makkelijker om te ontdekken wat er speelt en samen op zoek te gaan naar wat helpend kan zijn.",
    ],
  },
  {
    icon: Brain,
    title: "Autisme en ADHD",
    paragraphs: [
      "Iedereen voelt, denkt en verwerkt informatie op zijn eigen manier. Soms werkt het brein net iets anders. Dat kan uitdagingen met zich meebrengen, maar ook heel wat talenten en kwaliteiten.",
      "Bij De Kern is er ruimte voor kinderen, jongeren én volwassenen met onder andere autisme, ADHD of andere vormen van neurodivergentie. Je bent welkom, of je nu pas een diagnose kreeg, al langer, of gewoon op zoek bent naar meer inzicht in jezelf.",
      "Samen gaan we op zoek naar hoe jouw brein werkt. We kijken waar je krachten liggen en welke moeilijkheden je ervaart. Psycho-educatie helpt om jezelf beter te begrijpen en handvatten te vinden die echt bij jou passen.",
      "Ook ouders en andere belangrijke personen uit de omgeving kunnen ondersteuning krijgen. Wanneer de mensen rondom jou begrijpen hoe je de wereld ervaart en wat je nodig hebt, ontstaat er meer begrip en rust.",
    ],
  },
];

export const Aanbod = () => {
  const [open, setOpen] = useState(-1);

  return (
    <section id="aanbod" className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-32">
      <div className="grid lg:grid-cols-5 gap-8 lg:gap-16">
        <Reveal className="lg:col-span-2">
          <div className="lg:sticky lg:top-28">
            <SectionLabel className="mb-5">Ons aanbod</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-kern-dark leading-tight">
              Begeleiding die past bij elke levensfase.
            </h2>
            <p className="mt-6 max-w-md text-kern-taupe leading-relaxed">
              Vertrekkende vanuit jouw vragen en noden.
            </p>
          </div>
        </Reveal>

        <div className="lg:col-span-3">
          {offers.map((o, i) => {
            const Icon = o.icon;
            const isOpen = open === i;
            return (
              <Reveal key={o.title} delay={i * 0.06}>
                <div
                  data-testid={`aanbod-item-${i}`}
                  className="border-b border-kern-taupe/20 first:border-t"
                >
                  <button
                    data-testid={`aanbod-toggle-${i}`}
                    onClick={() => setOpen(i)}
                    className="w-full flex items-center gap-4 md:gap-5 py-5 md:py-7 text-left group"
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                        isOpen ? "bg-kern-terracotta" : "bg-kern-sage/25"
                      }`}
                    >
                      <Icon
                        size={20}
                        strokeWidth={1.7}
                        className={isOpen ? "text-kern-bg" : "text-kern-olive"}
                      />
                    </span>
                    <span className="flex-1 font-serif text-xl md:text-3xl text-kern-dark">
                      {o.title}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-kern-taupe shrink-0"
                    >
                      <Plus size={22} />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-0 sm:pl-[68px] pr-1 pb-6 md:pb-8 space-y-4 text-sm sm:text-base text-kern-taupe leading-relaxed">
                          {o.paragraphs.map((p, idx) => (
                            <p key={idx}>{p}</p>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
